// src/components/listing-leads/ListingLeadDrawer.jsx
// Listing lead detail panel. Restyled to mirror the Deals Board's detail view
// (wide, two-column, dark header) so clicking a pipeline card feels the same as
// clicking a board card — instead of the old narrow popup. Behaviour is
// unchanged: same actions (graduate / mark lost / delete / edit notes) and the
// same props (lead, canManage, onClose).
import React, { useEffect, useState } from "react";
import { X, Ban, CheckCircle2, Trash2, MoreVertical, MapPin, ExternalLink, FileText, Mail, FolderOpen } from "lucide-react";
import { toast } from "react-toastify";
import LeadConversation from "../leads/LeadConversation";
import LeadPortalForms from "../leads/LeadPortalForms";
import { useStateValue } from "../../context/StateProvider";
import AddClientPicker from "../new-contact/AddClientPicker";
import ContactEditDrawer from "../new-contact/ContactEditDrawer";
import {
  updateListingLead,
  convertListingLeadToDeal,
  deleteListingLead,
  getContactsByIds,
  setContactReferrer,
  classifyAppointmentForLead,
} from "../../utils/firebasefunctions";
import ReferredByPicker from "../lead-tracker/ReferredByPicker";
import { addContact, updateContact } from "../../utils/contactsApi";
import { maybeSendPortalLinkText } from "../../utils/texting";
import { normalizeContact } from "../../utils/contactNormalize";
import {
  listClients,
  addCoClient,
  removeClient,
  clientNames,
} from "../../utils/leadClients";
import {
  STAGE_COLORS,
  GRADUATION_STAGE,
  LISTING_STAGES,
  PARKING_STAGES,
} from "../../utils/listingLeadStages";
import {
  systemSourceOptions,
  leadSourceLabel,
  resolveSystemSource,
} from "../../utils/leadSourceLabel";
import EditableNotes from "../reuseable/EditableNotes";
import { notifyDealMilestone } from "../../utils/helper";
import { LISTING_FORM_TYPES } from "../../utils/portalForms";
import { requestSellerFile } from "../../utils/sellerFileRequest";
import { resolveLeadFolderId, driveFolderUrl } from "../../utils/leadFolder";
import FileRequestDetailsDialog from "../FileRequestDetailsDialog";
import { sendSellerPortalForms } from "../../utils/sendSellerPortalForms";
import { resolveLeadRecipients } from "../../utils/portalRecipients";
import PortalRecipientsModal from "../email/PortalRecipientsModal";
import { excludeTestAgents } from "../../utils/testAgents";
import AddressAutocomplete from "../reuseable/AddressAutocomplete";
import { useActionDialog } from "../ActionDialog";

const fmtPrice = (n) => (n ? `$${Number(n).toLocaleString("en-US")}` : "");

// Coerce a possibly-object field into a safe display string. Some migrated
// leads stored the deal's leadsource/agent OBJECT where a string was expected;
// rendering that object directly throws React error #31 and blanks the page.
const text = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object")
    return v.name || [v.firstname, v.lastname].filter(Boolean).join(" ") || "";
  return "";
};

// Dates arrive as either epoch-ms (createdAt) or YYYY-MM-DD strings (appt /
// follow-up). Parse YYYY-MM-DD as LOCAL midnight so it doesn't slip a day.
const fmtDate = (v) => {
  if (v == null || v === "") return "";
  const s = String(v);
  let d;
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  else if (/^\d+$/.test(s)) d = new Date(Number(s));
  else d = new Date(s);
  if (!d || isNaN(d.getTime())) return text(v);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const initialsOf = (name) =>
  (text(name) || "")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Row = ({ k, v }) => {
  const val = text(v);
  return val ? (
    <div className="flex justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-800 font-medium text-right">{val}</span>
    </div>
  ) : null;
};

// A date row that's an inline <input type="date"> when the viewer can manage
// the lead (saves immediately via onSave), and read-only formatted text
// otherwise. Used for the milestone dates the agent updates as the lead moves
// through the pipeline (CMA, listing appointment, next follow-up).
const EditableDateRow = ({ k, value, canEdit, onSave }) => {
  const [val, setVal] = useState(value || "");
  const [flash, setFlash] = useState(false);
  useEffect(() => setVal(value || ""), [value]);

  if (!canEdit) {
    const disp = fmtDate(value);
    return disp ? <Row k={k} v={disp} /> : null;
  }

  const handle = async (e) => {
    const next = e.target.value;
    const prev = val;
    setVal(next);
    const res = await onSave(next);
    if (res && res.success === false) {
      setVal(prev);
      toast.error("Couldn't save the date — try again");
    } else {
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);
    }
  };

  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 text-sm gap-2">
      <span className="text-slate-500 flex items-center gap-1.5">
        {k}
        {flash && <span className="text-[10px] text-emerald-600">Saved ✓</span>}
      </span>
      <input
        type="date"
        value={val}
        onChange={handle}
        className="text-slate-800 font-medium bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-sm outline-none focus:border-[#1E293B]"
      />
    </div>
  );
};

// Parse a typed money/number string back into a stored number (or "" to clear).
const toNum = (v) => {
  const n = Number(String(v == null ? "" : v).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : "";
};

// Inline-editable text/number row: an input when the viewer can manage the lead
// (saves on blur / Enter), read-only formatted text otherwise. Mirrors
// EditableDateRow. `display` formats the read-only value (e.g. money).
// `address` turns the input into a Google Places box: pick a suggestion and
// the full address fills itself in (then saves on blur like any other row).
const EditableTextRow = ({ k, value, canEdit, onSave, type = "text", display, placeholder, address = false }) => {
  const [val, setVal] = useState(value == null ? "" : value);
  const [flash, setFlash] = useState(false);
  useEffect(() => setVal(value == null ? "" : value), [value]);

  if (!canEdit) {
    const shown = display ? display(value) : text(value);
    return shown ? <Row k={k} v={shown} /> : null;
  }

  const commit = async () => {
    // Skip a redundant write when the typed value normalizes to what's stored
    // (e.g. "$620,000" over a stored 620000) — avoids a wasted write + flicker.
    const same =
      type === "number"
        ? toNum(val) === toNum(value)
        : String(val) === String(value == null ? "" : value);
    if (same) return;
    const res = await onSave(val);
    if (res && res.success === false) {
      setVal(value == null ? "" : value);
      toast.error("Couldn't save — try again");
    } else {
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);
    }
  };

  // An address is far too long for the 58%-wide right-aligned box the other
  // rows use — "948 Hague Ave, St Paul, MN 55104" came out as "948 Hague A".
  // Give it the full row: label above, input below, reading left to right.
  if (address) {
    return (
      <div className="py-2 border-b border-slate-100 last:border-0 text-sm">
        <span className="text-slate-500 flex items-center gap-1.5 mb-1">
          {k}
          {flash && <span className="text-[10px] text-emerald-600">Saved ✓</span>}
        </span>
        <AddressAutocomplete
          value={val}
          onChange={setVal}
          // A picked suggestion should save immediately — the agent's next
          // click is usually elsewhere, which would otherwise lose it.
          onPlaceParts={(parts) => onSave(parts.formatted)}
          placeholder={placeholder}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          className="w-full text-slate-800 font-medium bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-sm outline-none focus:border-[#1E293B]"
        />
      </div>
    );
  }

  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 text-sm gap-2">
      <span className="text-slate-500 flex items-center gap-1.5 shrink-0">
        {k}
        {flash && <span className="text-[10px] text-emerald-600">Saved ✓</span>}
      </span>
      <input
        type="text"
        inputMode={type === "number" ? "decimal" : undefined}
        value={val}
        placeholder={placeholder}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="text-slate-800 font-medium bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-sm outline-none focus:border-[#1E293B] text-right w-[58%]"
      />
    </div>
  );
};

// Inline-editable select row: a dropdown when manageable, read-only text
// otherwise. `options` is [{ value, label }]; `display` formats the read value.
const EditableSelectRow = ({ k, value, canEdit, onSave, options, display, placeholder = "—" }) => {
  const [flash, setFlash] = useState(false);

  if (!canEdit) {
    const shown = display ? display(value) : text(value);
    return shown ? <Row k={k} v={shown} /> : null;
  }

  const handle = async (e) => {
    const res = await onSave(e.target.value);
    if (res && res.success === false) {
      toast.error("Couldn't save — try again");
    } else {
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);
    }
  };

  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 text-sm gap-2">
      <span className="text-slate-500 flex items-center gap-1.5 shrink-0">
        {k}
        {flash && <span className="text-[10px] text-emerald-600">Saved ✓</span>}
      </span>
      <select
        value={value == null ? "" : value}
        onChange={handle}
        className="text-slate-800 font-medium bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-sm outline-none focus:border-[#1E293B] max-w-[58%]"
      >
        <option value="">{placeholder}</option>
        {/* Keep the current value visible even if it's not in `options` (e.g. an
            assigned agent filtered out of the pool, or a legacy source). */}
        {value != null &&
          value !== "" &&
          !options.some((o) => String(o.value) === String(value)) && (
            <option value={value}>
              {(display ? display(value) : text(value)) || String(value)}
            </option>
          )}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const Card = ({ label, children, className = "" }) => (
  <div
    className={`bg-white border border-slate-200 rounded-xl p-4 ${className}`}
  >
    {label && (
      <h4 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
        {label}
      </h4>
    )}
    {children}
  </div>
);

// Co-listing agents — a second (or more) agent on the listing. Both the main
// and the co-listing agents see and can work the lead. Saves immediately.
const CoListingAgentsRow = ({ lead, agents, canEdit }) => {
  const save = async (patch) => {
    const res = await updateListingLead(lead.id, patch);
    if (!res?.success)
      toast.error("Couldn't save that change. Please try again.");
    return res;
  };
  const co = Array.isArray(lead.coAgents) ? lead.coAgents : [];
  if (!canEdit && co.length === 0) return null;
  const pool = excludeTestAgents(agents || []);
  return (
    <div className="py-2 border-b border-slate-100 last:border-0 text-sm">
      <div className="flex justify-between items-start gap-2">
        <span className="text-slate-500 pt-0.5">Co-Listing Agents</span>
        <div className="flex-1 flex flex-wrap gap-1.5 justify-end">
          {co.length === 0 ? (
            <span className="text-slate-400">—</span>
          ) : (
            co.map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1 bg-slate-50 text-linkColor rounded-full px-2 py-0.5 text-xs font-medium"
              >
                {c.name || c.id}
                {canEdit && (
                  <button
                    type="button"
                    className="text-linkColor hover:text-themeColor"
                    onClick={() =>
                      save({
                        coAgents: co.filter((x) => x.id !== c.id),
                      })
                    }
                  >
                    ×
                  </button>
                )}
              </span>
            ))
          )}
        </div>
      </div>
      {canEdit && (
        <select
          className="mt-2 w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700"
          value=""
          onChange={(e) => {
            const id = e.target.value;
            if (!id) return;
            const a = pool.find((x) => x.id === id);
            if (!a || co.some((x) => x.id === id)) return;
            const name =
              [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.id;
            save({ coAgents: [...co, { id, name }] });
          }}
        >
          <option value="">+ Add co-listing agent…</option>
          {pool
            .filter(
              (a) => a.id !== lead.assignedAgent && !co.some((c) => c.id === a.id),
            )
            .map((a) => (
              <option key={a.id} value={a.id}>
                {[a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.id}
              </option>
            ))}
        </select>
      )}
    </div>
  );
};

export default function ListingLeadDrawer({ lead, canManage, onClose, onStageSet }) {
  const save = async (patch) => {
    const res = await updateListingLead(lead.id, patch);
    if (!res?.success)
      toast.error("Couldn't save that change. Please try again.");
    return res;
  };
  const [{ user, users, employees, leadsource }] = useStateValue();
  const { confirm, prompt, dialog } = useActionDialog();
  const [busy, setBusy] = useState(false);
  // A file request fired from THIS drawer — keeps the button locked even if
  // the lead-side link write fails (the deal exists either way).
  const [requestedHere, setRequestedHere] = useState(false);
  const [formType, setFormType] = useState(lead.formType || "seller");
  const [sendRecipients, setSendRecipients] = useState(null); // recipient picker
  const [requestDetailsOpen, setRequestDetailsOpen] = useState(false); // file-request questions dialog
  const [menuOpen, setMenuOpen] = useState(false); // ⋯ menu (lost/delete)
  const [contact, setContact] = useState(null);
  const [coPickerOpen, setCoPickerOpen] = useState(false);
  const [creatingCoContact, setCreatingCoContact] = useState(false);
  const [primaryPickerOpen, setPrimaryPickerOpen] = useState(false);
  const [creatingPrimaryContact, setCreatingPrimaryContact] = useState(false);
  const [refPickerOpen, setRefPickerOpen] = useState(false);
  // The Drive folder behind the lead's requested file (Henry, 2026-09-02:
  // reachable right from this screen). Prefetched quietly so the button can
  // be a plain link; "" means not linked or not made yet.
  const [folderId, setFolderId] = useState("");
  const [folderBusy, setFolderBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    setFolderId("");
    if (!lead?.sourceDealId) return undefined;
    const look = () =>
      resolveLeadFolderId(lead.sourceDealId)
        .then((id) => {
          if (cancelled) return;
          if (id) setFolderId(id);
          // Just-requested case: the server trigger is still copying the
          // template folder — check once more shortly so the link appears
          // without reopening the drawer.
          else retryTimer = setTimeout(look, 6000);
        })
        .catch(() => {});
    look();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [lead?.id, lead?.sourceDealId]);

  // Click path for a folder we don't know yet: resolve fresh and build it if
  // it's genuinely missing (a request from before the Drive triggers), then
  // open. Once known, the button renders as a plain link instead.
  const handleOpenFolder = async () => {
    if (folderBusy) return;
    setFolderBusy(true);
    try {
      const id = await resolveLeadFolderId(lead.sourceDealId, { create: true });
      setFolderId(id);
      window.open(driveFolderUrl(id), "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Open folder failed:", e);
      toast.error(e.message || "Couldn't open the folder");
    } finally {
      setFolderBusy(false);
    }
  };


  // Pull the linked contact so the Client card can show email / phone — the
  // lead itself only stores contactId + contactName.
  useEffect(() => {
    let alive = true;
    if (!lead?.contactId) {
      setContact(null);
      return;
    }
    getContactsByIds([lead.contactId])
      .then((rows) => alive && setContact(normalizeContact(rows?.[0])))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [lead?.contactId]);

  // --- Co-clients: add/remove on the saved lead (realtime listener refreshes it).
  const persistClients = (next) =>
    save({
      contactId: next.contactId,
      contactName: next.contactName,
      coClients: next.coClients || [],
    });
  const addCoClientToLead = async (contactId) => {
    setCoPickerOpen(false);
    let name = "";
    try {
      name = normalizeContact((await getContactsByIds([contactId]))[0])?.name || "";
    } catch (_) {
      /* keep blank name */
    }
    await persistClients(addCoClient(lead, { contactId, contactName: name }));
  };
  const removeCoClient = (contactId) => persistClients(removeClient(lead, contactId));

  // Swap the primary client (seller). Keeps any co-clients; just replaces who's
  // first. The card/header read contactName, so denormalize the full name.
  const changePrimary = async (contactId) => {
    setPrimaryPickerOpen(false);
    let name = "";
    try {
      name = normalizeContact((await getContactsByIds([contactId]))[0])?.name || "";
    } catch (_) {
      /* keep blank name — still link the contactId */
    }
    return save({
      contactId,
      contactName: name,
      // Don't let the new primary also linger as a co-client (would list the
      // same person twice).
      coClients: (Array.isArray(lead.coClients) ? lead.coClients : []).filter(
        (c) => c && c.contactId !== contactId,
      ),
    });
  };
  const handleCreatedPrimaryContact = async (form) => {
    if (!user?.gmailtoken?.access_token) {
      toast.error("Connect Gmail in Settings before adding a new contact");
      return;
    }
    try {
      const created = await addContact({ user, contact: form });
      setCreatingPrimaryContact(false);
      if (created?.firebaseId) await changePrimary(created.firebaseId);
    } catch (e) {
      toast.error(e.message || "Failed to create contact");
    }
  };
  const handleCreatedCoContact = async (form) => {
    if (!user?.gmailtoken?.access_token) {
      toast.error("Connect Gmail in Settings before adding a new contact");
      return;
    }
    try {
      const created = await addContact({ user, contact: form });
      setCreatingCoContact(false);
      if (created?.firebaseId) await addCoClientToLead(created.firebaseId);
    } catch (e) {
      toast.error(e.message || "Failed to create contact");
    }
  };

  // Edit an EXISTING client's info without leaving the lead — same flow as
  // the buyer drawer. updateContact syncs to Google when connected and falls
  // back to Firestore-only when not, so no Gmail gate here (unlike create).
  const [editingClient, setEditingClient] = useState(null);
  const [savingClientEdit, setSavingClientEdit] = useState(false);
  const openEditClient = async (c) => {
    try {
      const [raw] = await getContactsByIds([c.contactId]);
      if (!raw) { toast.error("Couldn't load this contact."); return; }
      setEditingClient({ raw, isPrimary: !!c.primary });
    } catch (e) { toast.error("Couldn't load this contact."); }
  };
  const handleEditedClient = async (form) => {
    if (!editingClient || savingClientEdit) return;
    setSavingClientEdit(true);
    try {
      const { raw, isPrimary } = editingClient;
      await updateContact({ user, contact: raw, form });
      const newName =
        `${form?.firstName || ""} ${form?.lastName || ""}`.trim() ||
        normalizeContact({ ...raw, ...form })?.name ||
        "";
      if (isPrimary) {
        await save({ contactName: newName });
        setContact(normalizeContact({ ...raw, ...form }));
      } else {
        await save({
          coClients: (Array.isArray(lead.coClients) ? lead.coClients : []).map(
            (cc) =>
              cc && cc.contactId === raw.id
                ? { ...cc, contactName: newName }
                : cc,
          ),
        });
      }
      setEditingClient(null);
      toast.success("Contact updated");
    } catch (e) {
      toast.error(e.message || "Failed to update contact");
    } finally {
      setSavingClientEdit(false);
    }
  };

  // Graduation happens in two steps: the button opens a small dialog asking
  // for the target Coming Soon date (the Pre-Market column files cards into
  // year sections by it), then the dialog confirms — or skips — and converts.
  const [gradOpen, setGradOpen] = useState(false);
  const [gradDate, setGradDate] = useState("");

  const handleGraduate = async (comingSoonDate) => {
    setBusy(true);
    const res = await convertListingLeadToDeal(
      lead,
      {
        // Deals store the full system lead-source object; resolve the lead's
        // stored id (or legacy text) so commission tracking keeps working.
        leadsource:
          resolveSystemSource(lead.source, leadsource) || lead.source || "",
        ...(comingSoonDate ? { listingAppointment: comingSoonDate } : {}),
      },
      user,
    );
    setBusy(false);
    if (res.success) {
      notifyDealMilestone(
        {
          agentData: lead.assignedAgent,
          propertyaddress: lead.propertyAddress,
          createdat: res.dealId,
        },
        "Pre-Market",
        users,
        user,
      ).catch(() => {});
      toast.success("Listed! Moved to the Deals Board (Pre-Market).");
      setGradOpen(false);
      onClose();
    } else toast.error(res.error || "Failed to graduate");
  };

  const handleMarkLost = async () => {
    // Cancelling still marks it lost, with no reason — the behaviour this has
    // always had. The board's own ⋮ menu bails on cancel instead.
    const reason =
      (await prompt({
        title: "Mark as Lost",
        label: "Reason this listing is lost or inactive (optional)",
        placeholder: "Went with another agent",
        confirmLabel: "Mark as Lost",
      })) || "";
    setBusy(true);
    const res = await save({
      status: "lost",
      lostReason: reason,
      lostAt: Date.now(),
    });
    setBusy(false);
    if (res.success) {
      // The Goals log classifies its appointment itself — lost lead, lost
      // appointment. Best-effort; never blocks the drawer.
      classifyAppointmentForLead(lead, "lost");
      toast.success("Marked as Lost");
      onClose();
    } else toast.error("Failed");
  };

  const handleDelete = async () => {
    const sure = await confirm({
      title: "Delete Lead",
      body: `Delete ${lead.contactName || "this lead"} from the pipeline?`,
      note: "This removes them from the board entirely.",
      tone: "danger",
      confirmLabel: "Delete",
    });
    if (!sure) return;
    setBusy(true);
    const res = await deleteListingLead(lead.id);
    setBusy(false);
    if (res.success) {
      toast.success("Lead deleted");
      onClose();
    } else toast.error("Failed to delete");
  };

  // Request a listing file for this already-existing lead — same flow as the
  // "Also Request a File" checkbox in NewListingLeadModal, just fired later
  // in the lead's life instead of at creation time.
  const handleRequestFile = () => {
    // Entry guard: never double-create, even if a stale render left the
    // button clickable (e.g. another staffer just requested on this lead).
    if (busy || requestedHere || lead.sourceDealId || lead.fileRequested) return;
    // Ask the file-request questions first (format, deadline, trust, …) so the
    // team gets a complete request instead of a shell to chase down.
    setRequestDetailsOpen(true);
  };
  const confirmRequestFile = async (details) => {
    setBusy(true);
    try {
      // requestSellerFile mints the file (Drive folder), links the lead, emails
      // recipients, and carries the chosen Form Type so the portal assembles the
      // right property-type forms. It's idempotent, so the drawer can lock after.
      await requestSellerFile(lead, user, { sendForms: false, formType, details });
      setRequestedHere(true);
      setRequestDetailsOpen(false);
      toast.success("Listing file requested");
    } catch (e) {
      console.error("Request file failed:", e);
      // Show what actually went wrong. A missing property address is a fixable
      // mistake, and "try again" sends the agent round in circles.
      toast.error(e?.message || "Couldn't request the file — try again");
    } finally {
      setBusy(false);
    }
  };
  // Open the recipient picker (everyone on the file, pre-checked).
  const handleSendSellerForms = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const recipients = await resolveLeadRecipients(lead);
      setSendRecipients(recipients);
    } catch (e) {
      console.error("Load recipients failed:", e);
      toast.error("Couldn't load the client list — try again");
    } finally {
      setBusy(false);
    }
  };
  // Confirmed in the picker → mint the file (idempotent) and send to the chosen.
  // `channel` is "both" | "email" | "text" — the agent's pick in the modal.
  const confirmSendSellerForms = async (selected, channel = "both") => {
    setBusy(true);
    try {
      const chosen = Array.isArray(selected) ? selected : [];
      const wantsEmail = channel !== "text";
      const wantsText = channel !== "email";
      const recipientEmails = wantsEmail
        ? chosen.map((r) => r?.email).filter(Boolean)
        : [];
      if (wantsEmail && !recipientEmails.length) {
        toast.error("None of the selected clients has an email on file.");
        setBusy(false);
        return;
      }
      const { deal } = await requestSellerFile(lead, user, {
        sendForms: false,
        formType,
      });
      setRequestedHere(true);
      const sent = await sendSellerPortalForms(deal, lead, user, { recipientEmails });
      // Text each picked client their own copy of the link (Settings switch
      // gated, never in demo) — parity with the buyer drawer.
      if (wantsText && sent.ok && !sent.demo && sent.url) {
        chosen
          .filter((r) => r?.contactId)
          .forEach((r) => {
            maybeSendPortalLinkText({
              leadId: lead.id,
              contactId: r.contactId,
              contactName: r.name || "",
              url: sent.url,
            }).catch(() => {});
          });
      }
      if (sent.ok)
        toast.success(
          sent.demo
            ? "Seller forms sent (demo)"
            : recipientEmails.length
              ? `Seller forms sent to ${recipientEmails.length}`
              : "Portal link ready — texting it now",
        );
      else toast.error(`Couldn't send forms: ${sent.reason || "unknown error"}`);
      setSendRecipients(null);
    } catch (e) {
      console.error("Send seller forms failed:", e);
      // This path mints the file too, so it can fail for a missing property
      // address — surface that instead of a dead-end "failed" toast.
      toast.error(e?.message || "Failed to send seller forms");
    } finally {
      setBusy(false);
    }
  };

  const agentOptions = excludeTestAgents(employees || []).map((a) => ({
    value: a.id,
    label:
      [a.firstname, a.lastname].filter(Boolean).join(" ") || a.name || a.email || a.id,
  }));

  const clientName = text(lead.contactName) || "Listing Lead";
  const hasCoClients =
    Array.isArray(lead.coClients) && lead.coClients.some((c) => c && c.contactId);
  const headerName = hasCoClients ? clientNames(lead) : clientName;
  const stageColor = STAGE_COLORS[lead.stage] || "#94a3b8";
  const address = text(lead.propertyAddress);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto">
          {/* Header — same dark bar as the board's Deal Details */}
          <div className="bg-[#1E293B] text-white px-6 py-4 flex items-start justify-between shrink-0">
            <div>
              <div className="text-lg font-bold flex items-center gap-2">
                {lead.rating && (
                  <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-white/20">
                    {lead.rating}
                  </span>
                )}
                {headerName}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <span
                  className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full text-white"
                  style={{ backgroundColor: `${stageColor}55` }}
                >
                  {lead.stage}
                </span>
                {lead.status && lead.status !== "active" && (
                  <span className="text-xs text-slate-300 capitalize">
                    {lead.status}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center transition"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body — two columns, mirrors the board overview layout */}
          <div className="flex-1 overflow-y-auto bg-slate-50 p-4 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4">
            {/* LEFT */}
            <div className="flex flex-col gap-4">
              <Card label="Listing Details">
                <EditableTextRow
                  k="Property Address"
                  value={lead.propertyAddress}
                  canEdit={canManage}
                  address
                  placeholder="Start typing an address…"
                  onSave={(v) => save({ propertyAddress: v })}
                />
                {/* Sits with the address it opens. Used to live in a separate
                    Location card whose only other content was a SECOND box
                    editing this same field. */}
                {address && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mb-2 text-xs font-semibold text-linkColor hover:text-themeColor"
                  >
                    <MapPin size={12} /> Open in Google Maps <ExternalLink size={12} />
                  </a>
                )}
                <EditableTextRow
                  k="Estimated Value"
                  value={lead.estValue}
                  type="number"
                  canEdit={canManage}
                  display={fmtPrice}
                  placeholder="$620,000"
                  onSave={(v) => save({ estValue: toNum(v) })}
                />
                <EditableTextRow
                  k="List Price"
                  value={lead.listPrice}
                  type="number"
                  canEdit={canManage}
                  display={fmtPrice}
                  onSave={(v) => save({ listPrice: toNum(v) })}
                />
                <EditableSelectRow
                  k="Source"
                  value={lead.source}
                  canEdit={canManage}
                  options={systemSourceOptions(leadsource)}
                  display={(v) => leadSourceLabel(v, leadsource) || text(v)}
                  onSave={(v) => save({ source: v })}
                />
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-slate-500">Referred By</span>
                  <span className="flex items-center gap-2 text-sm text-slate-800">
                    {contact?.referredByName || <span className="text-slate-400">None</span>}
                    {canManage && (
                      <button
                        type="button"
                        className="text-xs text-linkColor hover:underline"
                        onClick={() => setRefPickerOpen(true)}
                      >
                        {contact?.referredByName ? "Change" : "Set"}
                      </button>
                    )}
                    {canManage && contact?.referredByName && (
                      <button
                        type="button"
                        className="text-xs text-slate-400 hover:underline"
                        onClick={async () => {
                          const r = await setContactReferrer(lead.contactId, null);
                          if (r.success) {
                            setContact({ ...contact, referredByContactId: "", referredByName: "" });
                          } else {
                            toast.error("Couldn't clear referrer");
                          }
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </span>
                </div>
                {refPickerOpen && (
                  <ReferredByPicker
                    excludeContactId={lead.contactId}
                    onClose={() => setRefPickerOpen(false)}
                    onDone={async (ref) => {
                      setRefPickerOpen(false);
                      const r = await setContactReferrer(lead.contactId, ref);
                      if (r.success) {
                        setContact({
                          ...(contact || {}),
                          referredByContactId: ref.contactId,
                          referredByName: ref.name,
                        });
                      } else {
                        toast.error("Couldn't save referrer");
                      }
                    }}
                  />
                )}
                <EditableSelectRow
                  k="Rating"
                  value={lead.rating}
                  canEdit={canManage}
                  options={[
                    { value: "A", label: "A" },
                    { value: "B", label: "B" },
                    { value: "C", label: "C" },
                  ]}
                  onSave={(v) => save({ rating: v })}
                />
                <EditableDateRow
                  k="CMA Date"
                  value={lead.cmaDate}
                  canEdit={canManage}
                  onSave={(v) => save({ cmaDate: v })}
                />
                <EditableDateRow
                  k="Listing Appt"
                  value={lead.appointmentAt}
                  canEdit={canManage}
                  onSave={(v) => save({ appointmentAt: v })}
                />
              </Card>

              <Card label="Pipeline & Follow-Up">
                <EditableSelectRow
                  k="Stage"
                  value={lead.stage}
                  canEdit={canManage}
                  placeholder="New Lead"
                  options={LISTING_STAGES.map((s) => ({ value: s, label: s }))}
                  onSave={async (v) => {
                    const newStage = v || "New Lead";
                    const res = await save({ stage: newStage });
                    // Same "Appt Set" offer as the board paths (parent owns the prompt).
                    if (res?.success !== false) onStageSet?.(lead, newStage);
                    return res;
                  }}
                />
                <EditableSelectRow
                  k="Assigned Agent"
                  value={lead.assignedAgent}
                  canEdit={canManage}
                  options={agentOptions}
                  display={() => text(lead.assignedAgentName)}
                  onSave={(v) => {
                    const a = (employees || []).find((e) => e.id === v);
                    const name = a
                      ? [a.firstname, a.lastname].filter(Boolean).join(" ") ||
                        a.name ||
                        a.email ||
                        ""
                      : "";
                    return save({
                      assignedAgent: v,
                      assignedAgentName: name,
                    });
                  }}
                />
                <CoListingAgentsRow lead={lead} agents={employees} canEdit={canManage} />
                <EditableDateRow
                  k="Next Follow-Up"
                  value={lead.nextFollowUp}
                  canEdit={canManage}
                  onSave={(v) => save({ nextFollowUp: v })}
                />
                {/* Opt-in FUB sync: OFF keeps this lead out of Follow Up Boss;
                    flipping ON also pushes it right away (the update's push
                    call passes the backend's fubSync gate once saved). */}
                <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0 text-sm gap-2">
                  <span className="text-slate-500">Sync to Follow Up Boss</span>
                  <input
                    type="checkbox"
                    checked={lead.fubSync === true}
                    disabled={!canManage}
                    onChange={(e) =>
                      save({ fubSync: e.target.checked })
                    }
                  />
                </div>
                {(lead.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {lead.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* RIGHT */}
            <div className="flex flex-col gap-4">
              <Card label="Client">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-slate-100 text-linkColor flex items-center justify-center font-semibold text-sm shrink-0">
                    {initialsOf(clientName) || "—"}
                  </div>
                  <div className="min-w-0">
                    {/* Clicking the name opens the contact, same as Edit. */}
                    {canManage && lead.contactId ? (
                      <button
                        type="button"
                        onClick={() =>
                          openEditClient({ contactId: lead.contactId, primary: true })
                        }
                        className="text-sm font-semibold text-slate-800 truncate text-left hover:text-linkColor hover:underline max-w-full"
                      >
                        {clientName}
                      </button>
                    ) : (
                      <div className="text-sm font-semibold text-slate-800 truncate">
                        {clientName}
                      </div>
                    )}
                    {contact?.email && (
                      <div className="text-xs text-slate-500 truncate">
                        {contact.email}
                      </div>
                    )}
                    {contact?.phone && (
                      <div className="text-xs text-slate-500">
                        {contact.phone}
                      </div>
                    )}
                  </div>
                  {canManage && (
                    <span className="ml-auto shrink-0 flex items-center gap-3">
                      <button
                        onClick={() =>
                          openEditClient({ contactId: lead.contactId, primary: true })
                        }
                        className="text-xs font-semibold text-sky-600 hover:text-sky-700"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setPrimaryPickerOpen(true)}
                        className="text-xs font-semibold text-linkColor hover:text-themeColor"
                      >
                        Change
                      </button>
                    </span>
                  )}
                </div>
                {listClients(lead)
                  .filter((c) => !c.primary)
                  .map((c) => (
                    <div key={c.contactId} className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 text-sm">
                      {/* Clicking the name opens the contact, same as Edit. */}
                      {canManage ? (
                        <button
                          type="button"
                          onClick={() => openEditClient(c)}
                          className="text-slate-700 text-left hover:text-linkColor hover:underline"
                        >
                          {c.contactName || "(unnamed)"}
                        </button>
                      ) : (
                        <span className="text-slate-700">{c.contactName || "(unnamed)"}</span>
                      )}
                      {canManage && (
                        <span className="flex items-center gap-3">
                          <button onClick={() => openEditClient(c)}
                            className="text-xs font-semibold text-sky-600">Edit</button>
                          <button onClick={() => removeCoClient(c.contactId)}
                            className="text-xs text-slate-400 hover:text-red-500">Remove</button>
                        </span>
                      )}
                    </div>
                  ))}
                {canManage && (
                  <button onClick={() => setCoPickerOpen(true)}
                    className="text-sm font-semibold text-sky-600 mt-2">+ Add Co-Client</button>
                )}
              </Card>

              <Card label="Notes">
                <EditableNotes
                  value={lead.notes}
                  canEdit={canManage}
                  hideLabel
                  onSave={(notes) => save({ notes })}
                />
              </Card>

              <Card label="Activity">
                <div className="text-xs text-slate-600 flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Stage: {lead.stage}
                  </div>
                  {lead.createdAt && (
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                      Created · {fmtDate(lead.createdAt)}
                    </div>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100">
                  {/* Did they actually fill the portal forms out? Reads the
                      file this lead created (lead.sourceDealId). */}
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
                    Client Forms
                  </div>
                  <LeadPortalForms lead={lead} side="seller" />
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100">
                  {/* Texts + sent emails + logged activity, one timeline. */}
                  <LeadConversation lead={lead} />
                </div>
              </Card>
            </div>
          </div>

          {/* Footer — same actions as before */}
          {canManage && (
            <div className="px-6 py-3 border-t border-slate-200 bg-white flex flex-wrap items-center gap-2 shrink-0">
              {lead.status === "active" && (() => {
                // Requested = a file request exists (from here or the modal).
                // A lead moved in from the board also carries sourceDealId but
                // was never "requested" — its file already exists on the board.
                const requested = requestedHere || !!lead.fileRequested;
                const hasFile = requested || !!lead.sourceDealId;
                return (
                  <button
                    onClick={handleRequestFile}
                    disabled={busy || hasFile}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2 disabled:opacity-60 whitespace-nowrap ${hasFile ? "bg-amber-100 text-amber-700 border border-amber-300" : "bg-slate-700 text-white"}`}
                  >
                    <FileText size={14} />{" "}
                    {requested ? "File Requested ✓" : hasFile ? "File Linked ✓" : "Request Listing File"}
                  </button>
                );
              })()}
              {/* The listing's Drive folder — exists once a file is linked.
                 A plain link when the id is known; otherwise a click
                 resolves (and if needed builds) it first. */}
              {lead.status === "active" && lead.sourceDealId ? (
                folderId ? (
                  <a
                    href={driveFolderUrl(folderId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-700 text-white rounded-lg px-3 py-2 no-underline whitespace-nowrap"
                  >
                    <FolderOpen size={14} /> Listing Folder
                  </a>
                ) : (
                  <button
                    onClick={handleOpenFolder}
                    disabled={folderBusy}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-700 text-white rounded-lg px-3 py-2 disabled:opacity-60 whitespace-nowrap"
                  >
                    <FolderOpen size={14} /> {folderBusy ? "Opening…" : "Listing Folder"}
                  </button>
                )
              ) : null}

              {lead.status === "active" && (
                <>
                  <select
                    value={formType}
                    onChange={(e) => setFormType(e.target.value)}
                    disabled={busy}
                    title="Form Type — decides which forms the seller fills out"
                    className="text-xs font-semibold border border-slate-300 text-slate-700 rounded-lg px-2 py-2 bg-white disabled:opacity-60"
                  >
                    {LISTING_FORM_TYPES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleSendSellerForms}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-700 text-white rounded-lg px-3 py-2 disabled:opacity-60 whitespace-nowrap"
                  >
                    <Mail size={14} /> Send Seller Forms
                  </button>
                </>
              )}
              {lead.status === "active" &&
                (lead.stage === GRADUATION_STAGE ||
                  PARKING_STAGES.includes(lead.stage)) && (
                  <button
                    onClick={() => {
                      setGradDate("");
                      setGradOpen(true);
                    }}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold bg-emerald-600 text-white rounded-lg px-4 py-2 disabled:opacity-60"
                  >
                    <CheckCircle2 size={15} /> Sign the Listing → Deals Board
                  </button>
                )}
              {gradOpen && (
                <>
                  <div
                    className="fixed inset-0 bg-black/40 z-[60]"
                    onClick={() => !busy && setGradOpen(false)}
                  />
                  <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5 pointer-events-auto">
                      <div className="text-base font-semibold text-slate-900 mb-1">
                        Congrats on the signing 🎉
                      </div>
                      <p className="text-sm text-slate-600 mb-3">
                        When is this home planned to go Coming Soon? A target
                        is fine — you can change it later.
                      </p>
                      <label className="block text-xs font-medium text-slate-500 mb-1">
                        Target Coming Soon date
                      </label>
                      <input
                        type="date"
                        value={gradDate}
                        onChange={(e) => setGradDate(e.target.value)}
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mb-4 focus:outline-none focus:border-[#1E293B]"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleGraduate("")}
                          disabled={busy}
                          className="text-sm text-slate-600 hover:text-slate-800 px-3 py-2 disabled:opacity-60"
                        >
                          Skip for now
                        </button>
                        <button
                          onClick={() => handleGraduate(gradDate)}
                          disabled={busy}
                          className="text-sm font-semibold bg-[#1E293B] text-white rounded-lg px-4 py-2 disabled:opacity-60"
                        >
                          Move to Deals Board
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {/* ⋯ — negative actions. Opens upward (bottom bar). */}
              <div className="relative ml-auto">
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-label="More Actions"
                  className="inline-flex items-center text-sm font-semibold border border-slate-300 text-slate-600 rounded-lg px-2.5 py-2 hover:bg-slate-50"
                >
                  <MoreVertical size={16} />
                </button>
                {menuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setMenuOpen(false)}
                    />
                    <div className="absolute right-0 bottom-full mb-1 z-20 w-44 bg-white rounded-lg shadow-xl border border-slate-200 py-1">
                      {lead.status === "active" && (
                        <button
                          onClick={() => {
                            setMenuOpen(false);
                            handleMarkLost();
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <Ban size={14} /> Mark as Lost
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          handleDelete();
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <Trash2 size={14} /> Delete Lead
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {coPickerOpen && (
        <AddClientPicker
          userId={user.id}
          existingCustomerIds={listClients(lead).map((c) => c.contactId)}
          onClose={() => setCoPickerOpen(false)}
          onPick={addCoClientToLead}
          onCreateNew={() => { setCoPickerOpen(false); setCreatingCoContact(true); }}
        />
      )}
      {creatingCoContact && (
        <ContactEditDrawer
          onClose={() => setCreatingCoContact(false)}
          onSubmit={handleCreatedCoContact}
        />
      )}
      {primaryPickerOpen && (
        <AddClientPicker
          userId={user.id}
          existingCustomerIds={listClients(lead).map((c) => c.contactId)}
          onClose={() => setPrimaryPickerOpen(false)}
          onPick={changePrimary}
          onCreateNew={() => {
            setPrimaryPickerOpen(false);
            setCreatingPrimaryContact(true);
          }}
        />
      )}
      {creatingPrimaryContact && (
        <ContactEditDrawer
          onClose={() => setCreatingPrimaryContact(false)}
          onSubmit={handleCreatedPrimaryContact}
        />
      )}
      {editingClient && (
        <ContactEditDrawer
          initial={editingClient.raw}
          saving={savingClientEdit}
          onClose={() => setEditingClient(null)}
          onSubmit={handleEditedClient}
        />
      )}
      {sendRecipients && (
        <PortalRecipientsModal
          title="Send seller forms"
          recipients={sendRecipients}
          busy={busy}
          onCancel={() => setSendRecipients(null)}
          onSend={confirmSendSellerForms}
        />
      )}
      {requestDetailsOpen && (
        <FileRequestDetailsDialog
          side="seller"
          busy={busy}
          onCancel={() => setRequestDetailsOpen(false)}
          onSubmit={confirmRequestFile}
        />
      )}
      {dialog}
    </>
  );
}
